import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Car, DollarSign, Star, TrendingUp, Package, MapPin, User, CheckCircle, Clock, Loader2, RefreshCw, Banknote, CreditCard, AlertCircle, Wifi, WifiOff, Settings, Mail, LogOut, X, Shield, Bell, Edit, MessageCircle, FileText, Gift, Copy } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import axios from 'axios';
import ActiveRidePanel from '../components/ActiveRidePanel';
import NotificationSettings from '../components/NotificationSettings';
import DriverProfileEdit from '../components/DriverProfileEdit';
import InstallPrompt from '../components/InstallPrompt';
import ProfilePictureUpload from '../components/ProfilePictureUpload';
import { playNotificationSound } from '../services/notificationSound';
import RideChat from '../components/RideChat';
import notificationManager from '../utils/notificationManager';

// Social sharing image URL
const OG_IMAGE_DRIVER = 'https://static.prod-images.emergentagent.com/jobs/e5897d24-f6fe-426c-9a64-f8bda4899382/images/8b46b5a28daa30a4f64c1f75ff4c93f90803b083672fa6850df8252c7fba0d7b.png';

const DriverPortal = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const logoUrl = 'https://customer-assets.emergentagent.com/job_transport-app-29/artifacts/c2qih6ut_J%20%26%20S%20Logo%2001%20%20%282%29_1024x1024.jpg';
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  // WebSocket URL uses /api/ws/ prefix to route through ingress for secure WSS support
  const WS_URL = BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://');
  
  // Helper function to get full image URL
  const getFullImageUrl = (imageUrl) => {
    if (!imageUrl) return null;
    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://') || imageUrl.startsWith('data:')) {
      return imageUrl;
    }
    if (imageUrl.startsWith('/api/')) {
      return `${BACKEND_URL}${imageUrl}`;
    }
    return `${BACKEND_URL}/api${imageUrl.startsWith('/') ? '' : '/'}${imageUrl}`;
  };
  
  // Auth state
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [showNotificationSettings, setShowNotificationSettings] = useState(false);
  const [showProfileEdit, setShowProfileEdit] = useState(false);
  const [referralStats, setReferralStats] = useState(null);
  
  const [isOnline, setIsOnline] = useState(false);
  const [currentDriver, setCurrentDriver] = useState(null);
  const [availableRides, setAvailableRides] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const [activeTab, setActiveTab] = useState('rides');
  const [connectStatus, setConnectStatus] = useState(null);
  const [isSettingUpBank, setIsSettingUpBank] = useState(false);
  const [isRequestingPayout, setIsRequestingPayout] = useState(false);
  const [payoutHistory, setPayoutHistory] = useState([]);
  const [activeRide, setActiveRide] = useState(null);
  const [paypalEmail, setPaypalEmail] = useState('');
  const [isUpdatingPaypal, setIsUpdatingPaypal] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [newChatMessage, setNewChatMessage] = useState(null);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);
  
  // Scheduled ride alert state
  const [scheduledRideAlert, setScheduledRideAlert] = useState(null);
  
  // Service mode: 'rides', 'deliveries', or 'both'
  const [serviceMode, setServiceMode] = useState(() => {
    return localStorage.getItem('driver_service_mode') || 'both';
  });
  
  // 1099 Tax form states
  const [driver1099Data, setDriver1099Data] = useState(null);
  const [is1099Loading, setIs1099Loading] = useState(false);
  const [selected1099Year, setSelected1099Year] = useState(new Date().getFullYear());
  
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const locationWatchRef = useRef(null);
  const fetchRidesRef = useRef(null); // Ref to store latest fetch function

  // Handle Stripe Connect callback
  useEffect(() => {
    const connectResult = searchParams.get('connect');
    if (connectResult === 'success') {
      toast({
        title: "Bank Account Setup Complete!",
        description: "You can now receive payouts",
      });
      fetchConnectStatus();
    } else if (connectResult === 'refresh') {
      toast({
        title: "Please Complete Setup",
        description: "Your bank account setup needs to be completed",
        variant: "destructive"
      });
    }
  }, [searchParams]);

  // Check if driver is logged in on mount and restore session
  useEffect(() => {
    const restoreSession = async () => {
      const driverId = localStorage.getItem('driver_id');
      const driverName = localStorage.getItem('driver_name');
      
      if (driverId) {
        setIsLoggedIn(true);
        
        // Try to fetch fresh driver data from server
        try {
          const response = await axios.get(`${BACKEND_URL}/api/drivers/${driverId}`);
          const driverData = response.data.driver || response.data;
          
          setCurrentDriver({
            id: driverData.id || driverId,
            name: driverData.name || driverName,
            email: driverData.email || localStorage.getItem('driver_email'),
            rating: driverData.rating || 4.9,
            totalRides: driverData.total_rides || 0,
            pendingPayout: driverData.pending_payout || 0,
            vehicleModel: driverData.vehicle_model || localStorage.getItem('driver_vehicle') || 'Your Vehicle',
            licensePlate: driverData.license_plate || '',
            joinDate: driverData.created_at || new Date().toISOString(),
            status: driverData.status || 'approved',
            paypalEmail: driverData.paypal_email || localStorage.getItem('driver_paypal_email') || '',
            profile_photo: driverData.profile_photo || driverData.profile_picture_url,
            online: driverData.online || false
          });
          setPaypalEmail(driverData.paypal_email || localStorage.getItem('driver_paypal_email') || '');
          setIsOnline(driverData.online || false);
          
          // Update localStorage with fresh data
          localStorage.setItem('driver_name', driverData.name || driverName);
          if (driverData.email) localStorage.setItem('driver_email', driverData.email);
          if (driverData.vehicle_model) localStorage.setItem('driver_vehicle', driverData.vehicle_model);
          if (driverData.paypal_email) localStorage.setItem('driver_paypal_email', driverData.paypal_email);
          if (driverData.profile_photo) localStorage.setItem('driver_photo', driverData.profile_photo);
          
        } catch (error) {
          console.error('Error restoring driver session:', error);
          // Use cached data if server fetch fails
          setCurrentDriver({
            id: driverId,
            name: driverName,
            email: localStorage.getItem('driver_email'),
            rating: 4.9,
            totalRides: 0,
            pendingPayout: 0,
            vehicleModel: localStorage.getItem('driver_vehicle') || 'Your Vehicle',
            licensePlate: '',
            joinDate: new Date().toISOString(),
            status: 'approved',
            paypalEmail: localStorage.getItem('driver_paypal_email') || '',
            profile_photo: localStorage.getItem('driver_photo') || null
          });
          setPaypalEmail(localStorage.getItem('driver_paypal_email') || '');
        }
      }
      
      setIsLoading(false);
    };
    
    restoreSession();
  }, [BACKEND_URL]);

  // Fetch driver referral stats
  const fetchReferralStats = async (driverId) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/referral/driver/stats/${driverId}`);
      setReferralStats(response.data);
    } catch (error) {
      console.error('Error fetching referral stats:', error);
    }
  };

  // Fetch referral stats when logged in
  useEffect(() => {
    if (isLoggedIn && currentDriver?.id) {
      fetchReferralStats(currentDriver.id);
    }
  }, [isLoggedIn, currentDriver?.id]);

  // Handle driver login
  const handleDriverLogin = async (e) => {
    e.preventDefault();
    setIsLoggingIn(true);
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/drivers/login?email=${encodeURIComponent(loginEmail)}&password=${encodeURIComponent(loginPassword)}`);
      
      const driverId = response.data.driver_id;
      localStorage.setItem('driver_id', driverId);
      localStorage.setItem('driver_name', response.data.name);
      localStorage.setItem('driver_email', response.data.email);
      localStorage.setItem('driver_status', response.data.status);
      
      setIsLoggedIn(true);
      
      // Fetch full driver profile to get profile_photo and other data
      try {
        const profileResponse = await axios.get(`${BACKEND_URL}/api/drivers/${driverId}`);
        const driverData = profileResponse.data.driver || profileResponse.data;
        
        // Save profile photo to localStorage
        if (driverData.profile_photo) {
          localStorage.setItem('driver_photo', driverData.profile_photo);
        }
        if (driverData.vehicle_model) {
          localStorage.setItem('driver_vehicle', driverData.vehicle_model);
        }
        if (driverData.paypal_email) {
          localStorage.setItem('driver_paypal_email', driverData.paypal_email);
        }
        
        setCurrentDriver({
          id: driverId,
          name: driverData.name || response.data.name,
          email: driverData.email || response.data.email,
          rating: driverData.rating || 4.9,
          totalRides: driverData.total_rides || 0,
          pendingPayout: driverData.pending_payout || 0,
          vehicleModel: driverData.vehicle_model || 'Your Vehicle',
          licensePlate: driverData.license_plate || '',
          joinDate: driverData.created_at || new Date().toISOString(),
          status: driverData.status || response.data.status,
          paypalEmail: driverData.paypal_email || '',
          profile_photo: driverData.profile_photo || driverData.profile_picture_url,
          online: driverData.online || false
        });
        setPaypalEmail(driverData.paypal_email || '');
        setIsOnline(driverData.online || false);
      } catch (profileError) {
        console.error('Error fetching profile after login:', profileError);
        // Fallback to basic data
        setCurrentDriver({
          id: driverId,
          name: response.data.name,
          email: response.data.email,
          rating: 4.9,
          totalRides: 0,
          pendingPayout: 0,
          vehicleModel: 'Your Vehicle',
          licensePlate: '',
          joinDate: new Date().toISOString(),
          status: response.data.status,
          paypalEmail: ''
        });
      }
      
      // Subscribe to push notifications for ride alerts
      notificationManager.init().then(() => {
        notificationManager.subscribe(driverId, 'driver');
      });
      
      toast({
        title: "Welcome Back!",
        description: `Ready to earn, ${response.data.name}!`,
      });
    } catch (error) {
      toast({
        title: "Login Failed",
        description: error.response?.data?.detail || "Invalid credentials",
        variant: "destructive"
      });
    } finally {
      setIsLoggingIn(false);
    }
  };

  // Handle driver logout
  const handleDriverLogout = () => {
    // Stop location tracking
    if (locationWatchRef.current) {
      navigator.geolocation.clearWatch(locationWatchRef.current);
      locationWatchRef.current = null;
    }
    
    localStorage.removeItem('driver_id');
    localStorage.removeItem('driver_name');
    localStorage.removeItem('driver_email');
    localStorage.removeItem('driver_status');
    localStorage.removeItem('driver_paypal_email');
    localStorage.removeItem('driver_photo');
    setIsLoggedIn(false);
    setCurrentDriver(null);
    setIsOnline(false);
    setCurrentLocation(null);
    wsConnectedRef.current = false;  // Reset WebSocket toast flag on logout
    toast({
      title: "Logged Out",
      description: "See you next time!",
    });
  };

  // Start GPS tracking
  const startLocationTracking = useCallback(() => {
    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported by your browser');
      toast({
        title: "Location Not Available",
        description: "Your browser doesn't support GPS location",
        variant: "destructive"
      });
      return;
    }

    // Request high accuracy GPS
    const options = {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 5000
    };

    // Watch position continuously
    locationWatchRef.current = navigator.geolocation.watchPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        setCurrentLocation({ lat: latitude, lng: longitude });
        setLocationError(null);
        
        // Send location to server
        if (currentDriver?.id && isOnline) {
          try {
            await axios.post(
              `${BACKEND_URL}/api/location/${currentDriver.id}`,
              { lat: latitude, lng: longitude }
            );
            console.log('Location updated:', latitude, longitude);
          } catch (error) {
            console.error('Error sending location:', error);
          }
        }
      },
      (error) => {
        console.error('Geolocation error:', error);
        let errorMsg = 'Unable to get your location';
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMsg = 'Please allow location access to go online';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMsg = 'Location information unavailable';
            break;
          case error.TIMEOUT:
            errorMsg = 'Location request timed out';
            break;
          default:
            errorMsg = 'An unknown error occurred';
        }
        setLocationError(errorMsg);
        toast({
          title: "Location Error",
          description: errorMsg,
          variant: "destructive"
        });
      },
      options
    );
  }, [currentDriver, isOnline, BACKEND_URL]);

  // Stop GPS tracking
  const stopLocationTracking = useCallback(() => {
    if (locationWatchRef.current) {
      navigator.geolocation.clearWatch(locationWatchRef.current);
      locationWatchRef.current = null;
    }
  }, []);

  // Start/stop location tracking when online status changes
  useEffect(() => {
    if (isOnline && currentDriver) {
      startLocationTracking();
    } else {
      stopLocationTracking();
    }
    
    return () => {
      stopLocationTracking();
    };
  }, [isOnline, currentDriver, startLocationTracking, stopLocationTracking]);

  // Update PayPal email
  const handleUpdatePaypalEmail = async () => {
    if (!currentDriver || !paypalEmail) return;
    
    setIsUpdatingPaypal(true);
    try {
      await axios.post(`${BACKEND_URL}/api/payouts/driver/${currentDriver.id}/paypal-email?paypal_email=${encodeURIComponent(paypalEmail)}`);
      localStorage.setItem('driver_paypal_email', paypalEmail);
      setCurrentDriver(prev => ({...prev, paypalEmail}));
      toast({
        title: "PayPal Email Updated!",
        description: "Your payouts will be sent to this email",
      });
    } catch (error) {
      console.error('Error updating PayPal email:', error);
      toast({
        title: "Update Failed",
        description: error.response?.data?.detail || "Failed to update PayPal email",
        variant: "destructive"
      });
    } finally {
      setIsUpdatingPaypal(false);
    }
  };

  // Fetch active ride
  const fetchActiveRide = useCallback(async () => {
    if (!currentDriver) return;
    try {
      const response = await axios.get(`${BACKEND_URL}/api/drivers/${currentDriver.id}/active-ride`);
      setActiveRide(response.data.active_ride);
    } catch (error) {
      console.error('Error fetching active ride:', error);
    }
  }, [BACKEND_URL, currentDriver]);

  // Fetch Connect status on load
  const fetchConnectStatus = useCallback(async () => {
    if (!currentDriver) return;
    try {
      const response = await axios.get(`${BACKEND_URL}/api/drivers/${currentDriver.id}/connect/status`);
      setConnectStatus(response.data);
      if (response.data.pending_payout !== undefined) {
        setCurrentDriver(prev => ({...prev, pendingPayout: response.data.pending_payout}));
      }
    } catch (error) {
      console.error('Error fetching connect status:', error);
    }
  }, [BACKEND_URL, currentDriver]);

  useEffect(() => {
    if (currentDriver) {
      fetchConnectStatus();
      fetchPayoutHistory();
      fetchActiveRide();
    }
  }, [currentDriver?.id, fetchActiveRide]);

  // Fetch payout history
  const fetchPayoutHistory = async () => {
    if (!currentDriver) return;
    try {
      const response = await axios.get(`${BACKEND_URL}/api/drivers/${currentDriver.id}/payouts/history`);
      setPayoutHistory(response.data.payouts || []);
    } catch (error) {
      console.error('Error fetching payout history:', error);
    }
  };

  // Fetch driver's 1099 tax data
  const fetch1099Data = async (year = selected1099Year) => {
    if (!currentDriver) return;
    try {
      setIs1099Loading(true);
      const response = await axios.get(`${BACKEND_URL}/api/tax/drivers/${currentDriver.id}/1099/${year}`);
      setDriver1099Data(response.data);
    } catch (error) {
      console.error('Error fetching 1099 data:', error);
      setDriver1099Data(null);
    } finally {
      setIs1099Loading(false);
    }
  };

  // WebSocket connection management
  const wsConnectedRef = useRef(false);  // Track if we've already shown the connected toast
  
  const connectWebSocket = useCallback(() => {
    if (!currentDriver || !WS_URL) return;
    
    // Use /api/ws/ path to route through ingress for secure WSS support
    const wsUrl = `${WS_URL}/api/ws/driver/${currentDriver.id}`;
    console.log('Connecting to WebSocket:', wsUrl);
    
    try {
      wsRef.current = new WebSocket(wsUrl);
      
      wsRef.current.onopen = () => {
        console.log('WebSocket connected');
        setWsConnected(true);
        // Only show toast on first connection, not on reconnects
        if (!wsConnectedRef.current) {
          wsConnectedRef.current = true;
          toast({
            title: "Connected",
            description: "Real-time notifications active",
          });
        }
      };
      
      wsRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('WebSocket message:', data);
          
          if (data.type === 'new_ride') {
            // Play notification sound for new ride
            playNotificationSound();
            
            // Add new ride to available rides
            setAvailableRides(prev => {
              const exists = prev.some(r => r.id === data.ride.id);
              if (!exists) {
                toast({
                  title: "🚗 New Ride Request!",
                  description: `${data.ride.pickup} → ${data.ride.dropoff}`,
                });
                return [data.ride, ...prev];
              }
              return prev;
            });
          } else if (data.type === 'ride_accepted') {
            // Remove accepted ride from list
            setAvailableRides(prev => prev.filter(r => r.id !== data.ride_id));
          } else if (data.type === 'chat_message' && data.message?.ride_id === activeRide?.id) {
            // Handle incoming chat message
            setNewChatMessage(data.message);
            // Play sound and show toast if chat is not open
            if (!showChat) {
              playNotificationSound();
              toast({
                title: `💬 Message from ${data.message.sender_name}`,
                description: data.message.message?.slice(0, 50) + (data.message.message?.length > 50 ? '...' : ''),
              });
            }
          }
        } catch (e) {
          console.error('Error parsing WebSocket message:', e);
        }
      };
      
      wsRef.current.onclose = () => {
        console.log('WebSocket disconnected');
        setWsConnected(false);
        
        // Attempt to reconnect if still online
        if (isOnline) {
          reconnectTimeoutRef.current = setTimeout(() => {
            console.log('Attempting to reconnect WebSocket...');
            connectWebSocket();
          }, 3000);
        }
      };
      
      wsRef.current.onerror = (error) => {
        console.error('WebSocket error:', error);
      };
    } catch (error) {
      console.error('Error creating WebSocket:', error);
    }
  }, [currentDriver, WS_URL, isOnline]);

  const disconnectWebSocket = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setWsConnected(false);
  }, []);

  // WebSocket keep-alive ping
  useEffect(() => {
    if (!wsConnected || !wsRef.current) return;
    
    const pingInterval = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send('ping');
      }
    }, 30000);
    
    return () => clearInterval(pingInterval);
  }, [wsConnected]);

  const fetchAvailableRides = useCallback(async () => {
    if (!currentDriver) return;
    
    try {
      setIsRefreshing(true);
      const response = await axios.get(`${BACKEND_URL}/api/drivers/${currentDriver.id}/available-rides`);
      const rides = response.data.rides || [];
      
      // Check for new rides and notify (using functional update to avoid dependency)
      setAvailableRides(prevRides => {
        if (rides.length > prevRides.length) {
          const newRides = rides.filter(r => !prevRides.some(ar => ar.id === r.id));
          if (newRides.length > 0) {
            playNotificationSound();
            toast({
              title: "🚗 New Ride Request!",
              description: `${newRides.length} new ride${newRides.length > 1 ? 's' : ''} available`,
            });
          }
        }
        return rides;
      });
    } catch (error) {
      console.error('Error fetching rides:', error);
    } finally {
      setIsRefreshing(false);
    }
  }, [BACKEND_URL, currentDriver]);

  // Keep ref updated with latest fetch function
  useEffect(() => {
    fetchRidesRef.current = fetchAvailableRides;
  }, [fetchAvailableRides]);

  // Fallback polling for rides (works across all timezones)
  // Using refs to avoid recreating intervals on every render
  useEffect(() => {
    if (!isOnline || !currentDriver) return;
    
    // Poll every 10 seconds as fallback when WebSocket might not be connected
    const pollInterval = setInterval(() => {
      if (!wsConnected && fetchRidesRef.current) {
        console.log('WebSocket not connected, polling for rides...');
        fetchRidesRef.current();
      }
    }, 10000);
    
    // Also poll every 30 seconds even when WebSocket is connected (as backup)
    const backupPollInterval = setInterval(() => {
      if (fetchRidesRef.current) {
        fetchRidesRef.current();
      }
    }, 30000);
    
    return () => {
      clearInterval(pollInterval);
      clearInterval(backupPollInterval);
    };
  }, [isOnline, currentDriver, wsConnected]); // Removed fetchAvailableRides dependency

  // Send driver location updates when online and have active ride
  useEffect(() => {
    if (!isOnline || !currentDriver || !activeRide) return;

    let watchId = null;

    // Function to send location to server
    const sendLocation = async (lat, lng) => {
      try {
        await axios.post(`${BACKEND_URL}/api/location/driver/${currentDriver.id}/update`, {
          lat,
          lng
        });
      } catch (error) {
        console.error('Error sending location:', error);
      }
    };

    // Watch position and send updates
    if ('geolocation' in navigator) {
      watchId = navigator.geolocation.watchPosition(
        (position) => {
          sendLocation(position.coords.latitude, position.coords.longitude);
        },
        (error) => {
          console.warn('Geolocation error:', error);
          // If geolocation fails, send a simulated location near the pickup
          if (activeRide?.pickup) {
            const jitter = () => (Math.random() - 0.5) * 0.01; // Small random offset
            sendLocation(
              activeRide.pickup.lat + jitter(),
              activeRide.pickup.lng + jitter()
            );
          }
        },
        { enableHighAccuracy: true, maximumAge: 5000, timeout: 10000 }
      );

      // Also send location every 15 seconds as backup
      const locationInterval = setInterval(() => {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            sendLocation(position.coords.latitude, position.coords.longitude);
          },
          () => {
            // Fallback if getCurrentPosition fails
            if (activeRide?.pickup) {
              const jitter = () => (Math.random() - 0.5) * 0.01;
              sendLocation(
                activeRide.pickup.lat + jitter(),
                activeRide.pickup.lng + jitter()
              );
            }
          }
        );
      }, 15000);

      return () => {
        if (watchId !== null) {
          navigator.geolocation.clearWatch(watchId);
        }
        clearInterval(locationInterval);
      };
    }
  }, [isOnline, currentDriver, activeRide, BACKEND_URL]);

  // Connect/disconnect WebSocket based on online status
  useEffect(() => {
    if (isOnline && currentDriver) {
      connectWebSocket();
      // Initial fetch using ref
      if (fetchRidesRef.current) {
        fetchRidesRef.current();
      }
    } else {
      disconnectWebSocket();
      setAvailableRides([]);
    }
    
    return () => disconnectWebSocket();
  }, [isOnline, currentDriver, connectWebSocket, disconnectWebSocket]); // Removed fetchAvailableRides

  // Request location in background (non-blocking)
  const requestLocationInBackground = async () => {
    if (!navigator.geolocation || !currentDriver) return;
    
    try {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          // Success - send location to server
          try {
            await axios.post(
              `${BACKEND_URL}/api/location/${currentDriver.id}`,
              { lat: position.coords.latitude, lng: position.coords.longitude }
            );
            setCurrentLocation({
              lat: position.coords.latitude,
              lng: position.coords.longitude
            });
            setLocationError(null);
          } catch (err) {
            console.error('Failed to send location:', err);
          }
        },
        (error) => {
          // Failed to get location - just log it, don't block
          console.log('Background location request failed:', error.message);
          setLocationError(error.message);
        },
        { enableHighAccuracy: false, timeout: 10000, maximumAge: 60000 }
      );
    } catch (err) {
      console.log('Geolocation error:', err);
    }
  };

  const handleGoOnlineOffline = async () => {
    if (!currentDriver) return;
    
    try {
      const newStatus = !isOnline;
      
      // Simply toggle online status - no location blocking
      await axios.post(`${BACKEND_URL}/api/drivers/online/${currentDriver.id}?online=${newStatus}`);
      setIsOnline(newStatus);
      
      if (newStatus) {
        // Going online
        toast({
          title: "You're Online! 🟢",
          description: "You will now receive ride requests.",
        });
        
        // Request location in background (non-blocking)
        requestLocationInBackground();
        
        // Show location tip if geolocation is available
        if (navigator.geolocation) {
          // Check if we can get location
          navigator.geolocation.getCurrentPosition(
            () => {
              // Location works, no need to show anything
            },
            (error) => {
              if (error.code === 1) {
                // Permission denied - show helpful tip
                setTimeout(() => {
                  toast({
                    title: "📍 Enable Location for Better Matching",
                    description: "Tap the lock icon in your browser's address bar and allow location for better ride matching.",
                  });
                }, 2000);
              }
            },
            { timeout: 5000 }
          );
        }
      } else {
        // Going offline
        toast({
          title: "You're Offline",
          description: "You won't receive new ride requests.",
        });
        setAvailableRides([]);
        setCurrentLocation(null);
      }
    } catch (error) {
      console.error('Error toggling status:', error);
      toast({
        title: "Error",
        description: "Failed to update status. Please try again.",
        variant: "destructive"
      });
    }
  };

  // Voice announcement for scheduled rides/deliveries
  const announceScheduledPickup = (scheduledTime, passengerName, isDelivery = false) => {
    if ('speechSynthesis' in window) {
      // Cancel any ongoing speech
      window.speechSynthesis.cancel();
      
      const formattedTime = new Date(scheduledTime).toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
      
      const serviceType = isDelivery ? 'delivery' : 'ride';
      const message = `Attention! This is a scheduled ${serviceType}. Pickup time is ${formattedTime} for ${passengerName || 'customer'}. Please arrive on time.`;
      
      const utterance = new SpeechSynthesisUtterance(message);
      utterance.rate = 0.9;
      utterance.pitch = 1;
      utterance.volume = 1;
      
      // Try to use a clear voice
      const voices = window.speechSynthesis.getVoices();
      const preferredVoice = voices.find(v => v.lang.includes('en') && v.name.includes('Google')) || voices[0];
      if (preferredVoice) {
        utterance.voice = preferredVoice;
      }
      
      window.speechSynthesis.speak(utterance);
    }
  };

  const handleAcceptRide = async (rideId) => {
    if (!currentDriver) return;
    
    // Find the ride to get its details
    const ride = availableRides.find(r => r.id === rideId);
    const isDelivery = ride?.type === 'delivery' || ride?.service_type === 'delivery';
    
    try {
      await axios.post(`${BACKEND_URL}/api/drivers/rides/accept`, {
        ride_id: rideId,
        driver_id: currentDriver.id
      });
      
      setAvailableRides(availableRides.filter(r => r.id !== rideId));
      
      // Check if this is a scheduled ride/delivery
      if (ride?.scheduled_time || ride?.is_scheduled) {
        const scheduledTime = ride.scheduled_time;
        const formattedTime = new Date(scheduledTime).toLocaleString('en-US', {
          weekday: 'short',
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        });
        
        // Show visual alert
        setScheduledRideAlert({
          passengerName: ride.passenger_name || 'Customer',
          pickupAddress: ride.pickup_address || ride.pickup?.address || 'See app for details',
          scheduledTime: formattedTime,
          rawTime: scheduledTime,
          isDelivery: isDelivery
        });
        
        // Voice announcement
        announceScheduledPickup(scheduledTime, ride.passenger_name, isDelivery);
        
        // Play notification sound too
        playNotificationSound();
        
        const serviceLabel = isDelivery ? 'DELIVERY' : 'RIDE';
        toast({
          title: `⏰ SCHEDULED ${serviceLabel} Accepted!`,
          description: `Pickup at ${formattedTime}`,
        });
      } else {
        const serviceLabel = isDelivery ? 'Delivery' : 'Ride';
        toast({
          title: `${serviceLabel} Accepted!`,
          description: "Navigate to pickup location now",
        });
      }
    } catch (error) {
      console.error('Error accepting ride:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to accept ride",
        variant: "destructive"
      });
    }
  };

  // Decline/Reject a ride - auto-assigns to next driver
  const handleDeclineRide = async (rideId) => {
    if (!currentDriver) return;
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/drivers/rides/${rideId}/reject?driver_id=${currentDriver.id}`);
      
      // Remove from available rides
      setAvailableRides(availableRides.filter(r => r.id !== rideId));
      
      if (response.data.new_driver_assigned) {
        toast({
          title: "Ride Declined",
          description: "Ride sent to next available driver",
        });
      } else {
        toast({
          title: "Ride Declined",
          description: "No other drivers available - Admin notified",
          variant: "warning"
        });
      }
    } catch (error) {
      console.error('Error declining ride:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to decline ride",
        variant: "destructive"
      });
    }
  };

  const handleSetupBankAccount = async () => {
    if (!currentDriver) return;
    
    setIsSettingUpBank(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/drivers/${currentDriver.id}/connect/onboard`);
      
      // Redirect to Stripe onboarding
      window.location.href = response.data.onboarding_url;
    } catch (error) {
      console.error('Error setting up bank account:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to start bank setup",
        variant: "destructive"
      });
      setIsSettingUpBank(false);
    }
  };

  const handleRequestPayout = async () => {
    if (!currentDriver || !connectStatus?.payouts_enabled) return;
    
    setIsRequestingPayout(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/drivers/${currentDriver.id}/payout/request`);
      
      toast({
        title: "Payout Initiated!",
        description: `$${response.data.amount} will be transferred to your bank account`,
      });
      
      // Refresh data
      await fetchConnectStatus();
      await fetchPayoutHistory();
    } catch (error) {
      console.error('Error requesting payout:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to request payout",
        variant: "destructive"
      });
    } finally {
      setIsRequestingPayout(false);
    }
  };

  const stats = [
    { label: 'Today\'s Earnings', value: `$${145.50}`, icon: DollarSign, color: 'text-green-600', bg: 'bg-green-100' },
    { label: 'Total Rides', value: currentDriver?.totalRides || 0, icon: Car, color: 'text-amber-600', bg: 'bg-amber-100' },
    { label: 'Rating', value: currentDriver?.rating || 0, icon: Star, color: 'text-yellow-600', bg: 'bg-yellow-100' },
    { label: 'Pending Payout', value: `$${(connectStatus?.pending_payout || currentDriver?.pendingPayout || 0).toFixed(2)}`, icon: TrendingUp, color: 'text-slate-700', bg: 'bg-slate-100' }
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-amber-50 flex items-center justify-center">
        <Loader2 className="h-16 w-16 text-amber-500 animate-spin" />
      </div>
    );
  }

  // Show login page if not logged in
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-slate-800 to-slate-900 flex flex-col">
        {/* SEO & Social Sharing Meta Tags */}
        <Helmet>
          <title>Drive with J-Style - Start Earning Today</title>
          <meta name="description" content="Become a J-Style driver. Flexible hours, competitive pay, and weekly payouts. Apply now!" />
          <meta property="og:type" content="website" />
          <meta property="og:title" content="Drive with J-Style - Start Earning Today" />
          <meta property="og:description" content="Join our team of professional drivers. Flexible hours, competitive pay, 100% tips, and weekly PayPal payouts." />
          <meta property="og:image" content={OG_IMAGE_DRIVER} />
          <meta property="og:url" content={window.location.href} />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content="Drive with J-Style" />
          <meta name="twitter:description" content="Start earning with flexible hours and competitive pay." />
          <meta name="twitter:image" content={OG_IMAGE_DRIVER} />
          <link rel="manifest" href="/manifest-driver.json" />
          <meta name="apple-mobile-web-app-capable" content="yes" />
          <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
          <meta name="apple-mobile-web-app-title" content="J-Style Driver" />
        </Helmet>

        {/* Header */}
        <header className="border-b border-white/10 bg-black/20 backdrop-blur-sm">
          <div className="container mx-auto px-4 py-4 flex justify-between items-center">
            <div className="flex items-center gap-3">
              <img src={logoUrl} alt="Logo" className="h-10 w-10 object-contain" />
              <div className="flex items-center gap-2">
                <Car size={20} className="text-amber-400" />
                <span className="text-xl font-bold text-white">J-Style Driver</span>
              </div>
            </div>
          </div>
        </header>

        <div className="flex-1 flex items-center justify-center p-6">
          <div className="w-full max-w-md">
            <Card className="p-8 border-0 shadow-2xl bg-white/95 backdrop-blur">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <Car size={32} className="text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Driver Login</h2>
                <p className="text-gray-500">Sign in to start accepting rides</p>
              </div>

              <form onSubmit={handleDriverLogin} className="space-y-5">
                <div>
                  <Label className="text-gray-700">Email Address</Label>
                  <Input 
                    type="email"
                    placeholder="driver@example.com"
                    value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                    required
                    className="mt-1.5 h-12 border-2 focus:border-blue-500"
                    data-testid="driver-login-email"
                  />
                </div>

                <div>
                  <Label className="text-gray-700">Password</Label>
                  <Input 
                    type="password"
                    placeholder="••••••••"
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    required
                    className="mt-1.5 h-12 border-2 focus:border-blue-500"
                    data-testid="driver-login-password"
                  />
                </div>

                <Button 
                  type="submit"
                  disabled={isLoggingIn}
                  className="w-full h-12 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold text-lg shadow-lg"
                  data-testid="driver-login-submit"
                >
                  {isLoggingIn ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Signing in...
                    </>
                  ) : (
                    'Sign In to Drive'
                  )}
                </Button>
              </form>

              <div className="mt-6 pt-6 border-t border-gray-100">
                <p className="text-center text-gray-600 mb-4">
                  Want to become a driver?
                </p>
                <Button 
                  variant="outline" 
                  onClick={() => navigate('/driver/signup')}
                  className="w-full h-12 border-2 border-amber-500 text-amber-600 hover:bg-amber-50 font-semibold"
                >
                  Apply to Drive
                </Button>
              </div>
            </Card>

            {/* Benefits */}
            <div className="mt-8 grid grid-cols-2 gap-4">
              <div className="bg-white/10 backdrop-blur rounded-xl p-4 text-center">
                <DollarSign className="mx-auto text-amber-400 mb-2" size={24} />
                <p className="text-white font-medium">Earn Big</p>
                <p className="text-gray-400 text-xs">100% tips</p>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-xl p-4 text-center">
                <Clock className="mx-auto text-amber-400 mb-2" size={24} />
                <p className="text-white font-medium">Flexible</p>
                <p className="text-gray-400 text-xs">Your hours</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-amber-50">
      {/* Notification Settings Modal */}
      <NotificationSettings 
        isOpen={showNotificationSettings} 
        onClose={() => setShowNotificationSettings(false)}
        userType="driver"
      />

      {/* Profile Edit Modal */}
      <DriverProfileEdit
        isOpen={showProfileEdit}
        onClose={() => setShowProfileEdit(false)}
        driverId={currentDriver?.id}
        backendUrl={BACKEND_URL}
        onUpdate={() => {
          // Refresh driver data
          const name = localStorage.getItem('driver_name');
          if (name && currentDriver) {
            setCurrentDriver(prev => ({ ...prev, name }));
          }
        }}
      />

      {/* Header */}
      <header className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-2 sm:px-4 py-3 sm:py-4 flex justify-between items-center gap-2">
          {/* Logo - smaller on mobile */}
          <div className="flex items-center gap-2 min-w-0">
            <img src={logoUrl} alt="Logo" className="h-8 w-8 sm:h-10 sm:w-10 object-contain flex-shrink-0" />
            <span className="text-base sm:text-xl md:text-2xl font-bold bg-gradient-to-r from-amber-600 to-yellow-500 bg-clip-text text-transparent whitespace-nowrap hidden xs:inline">J-Style</span>
          </div>
          
          {/* Right side controls */}
          <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
            {/* Icon buttons - compact on mobile */}
            <div className="flex items-center">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setShowProfileEdit(true)}
                className="text-gray-500 hover:text-amber-600 h-8 w-8 p-0 sm:h-9 sm:w-9"
                title="Edit Profile"
              >
                <Edit size={16} />
              </Button>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setShowNotificationSettings(true)}
                className="text-gray-500 hover:text-amber-600 h-8 w-8 p-0 sm:h-9 sm:w-9"
                title="Notification Settings"
              >
                <Bell size={16} />
              </Button>
            </div>
            
            {/* Status indicator */}
            <div className="flex items-center gap-1 px-2 py-1 bg-gray-100 rounded-full">
              {isOnline && wsConnected && (
                <Wifi size={12} className="text-green-500 hidden sm:block" />
              )}
              <div className={`w-2 h-2 sm:w-3 sm:h-3 rounded-full ${isOnline ? 'bg-green-500' : 'bg-gray-400'}`} />
              <span className="text-xs font-medium hidden sm:inline">{isOnline ? 'Online' : 'Offline'}</span>
            </div>
            
            {/* GPS Location indicator */}
            {isOnline && currentLocation && (
              <div className="hidden md:flex items-center gap-1 px-2 py-1 bg-green-50 rounded-full border border-green-200">
                <MapPin size={12} className="text-green-600" />
                <span className="text-xs text-green-700">GPS Active</span>
              </div>
            )}
            {isOnline && locationError && (
              <div className="hidden md:flex items-center gap-1 px-2 py-1 bg-orange-50 rounded-full border border-orange-200" title="Enable location in browser for better ride matching">
                <AlertCircle size={12} className="text-orange-500" />
                <span className="text-xs text-orange-600">GPS Off</span>
              </div>
            )}
            
            {/* Service Mode Toggle - Rides / Deliveries / Both */}
            {isOnline && (
              <div className="flex items-center bg-gray-100 rounded-lg p-0.5" data-testid="service-mode-toggle">
                <button
                  onClick={() => {
                    setServiceMode('rides');
                    localStorage.setItem('driver_service_mode', 'rides');
                    toast({ title: "Mode: Rides Only", description: "You'll only see ride requests" });
                  }}
                  className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition-all ${
                    serviceMode === 'rides' 
                      ? 'bg-amber-500 text-white shadow-sm' 
                      : 'text-gray-600 hover:bg-gray-200'
                  }`}
                  title="Rides Only"
                >
                  <Car size={14} />
                  <span className="hidden sm:inline">Rides</span>
                </button>
                <button
                  onClick={() => {
                    setServiceMode('both');
                    localStorage.setItem('driver_service_mode', 'both');
                    toast({ title: "Mode: All Requests", description: "You'll see rides and deliveries" });
                  }}
                  className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition-all ${
                    serviceMode === 'both' 
                      ? 'bg-amber-500 text-white shadow-sm' 
                      : 'text-gray-600 hover:bg-gray-200'
                  }`}
                  title="Both"
                >
                  <span className="text-xs">All</span>
                </button>
                <button
                  onClick={() => {
                    setServiceMode('deliveries');
                    localStorage.setItem('driver_service_mode', 'deliveries');
                    toast({ title: "Mode: Deliveries Only", description: "You'll only see delivery requests" });
                  }}
                  className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition-all ${
                    serviceMode === 'deliveries' 
                      ? 'bg-slate-700 text-white shadow-sm' 
                      : 'text-gray-600 hover:bg-gray-200'
                  }`}
                  title="Deliveries Only"
                >
                  <Package size={14} />
                  <span className="hidden sm:inline">Delivery</span>
                </button>
              </div>
            )}
            
            {/* Online/Offline toggle - Shows current status */}
            <Button 
              variant={isOnline ? 'destructive' : 'default'}
              onClick={handleGoOnlineOffline}
              className={`text-xs sm:text-sm px-2 sm:px-3 h-8 sm:h-9 ${!isOnline ? 'bg-gradient-to-r from-amber-500 to-yellow-600 hover:from-amber-600 hover:to-yellow-700' : 'bg-red-500 hover:bg-red-600'}`}
              size="sm"
              data-testid="driver-online-toggle"
            >
              {isOnline ? 'Go Offline' : 'Go Online'}
            </Button>
            
            {/* Logout */}
            <Button 
              variant="outline" 
              size="sm"
              onClick={handleDriverLogout}
              className="border-gray-300 h-8 w-8 sm:h-9 sm:w-auto p-0 sm:px-3"
            >
              <LogOut size={16} />
              <span className="hidden sm:inline ml-1">Logout</span>
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-2 sm:px-4 py-3 sm:py-6">
        {/* Driver Profile Card with Stats - v2 Mobile Layout */}
        <Card className="p-3 sm:p-6 mb-4 sm:mb-6 border-2 bg-gradient-to-r from-slate-50 to-amber-50">
          {/* Driver Avatar & Name - Stacked layout */}
          <div className="flex items-center gap-3 mb-4">
            <ProfilePictureUpload
              currentImage={currentDriver?.profile_photo || localStorage.getItem('driver_photo')}
              userId={currentDriver?.id}
              userType="driver"
              backendUrl={BACKEND_URL}
              size="md"
              onUploadSuccess={(url) => {
                localStorage.setItem('driver_photo', url);
                setCurrentDriver(prev => ({ ...prev, profile_photo: url }));
              }}
            />
            <div className="flex-1 min-w-0">
              <h2 className="text-base sm:text-xl font-bold text-gray-900 truncate">{currentDriver?.name || 'Driver'}</h2>
              <p className="text-xs sm:text-sm text-gray-500 truncate">{currentDriver?.email}</p>
            </div>
          </div>
          
          {/* Stats Grid - MUST be 2x2 on mobile, 4 columns on desktop */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }} className="sm:!grid-cols-4">
            {stats.map((stat) => {
              const Icon = stat.icon;
              return (
                <div key={stat.label} className="flex flex-col items-center p-2 bg-white rounded-lg shadow-sm text-center" data-testid={`stat-${stat.label.toLowerCase().replace(/['\s]/g, '-')}`}>
                  <div className={`w-8 h-8 ${stat.bg} rounded-lg flex items-center justify-center mb-1`}>
                    <Icon className={stat.color} size={14} />
                  </div>
                  <p className="text-sm font-bold leading-tight">{stat.value}</p>
                  <p className="text-[9px] text-gray-500 leading-tight">{stat.label}</p>
                </div>
              );
            })}
          </div>
        </Card>

        {/* Tab Navigation - Scrollable on mobile */}
        <div className="overflow-x-auto -mx-3 px-3 sm:mx-0 sm:px-0 mb-6">
          <div className="flex gap-2 min-w-max sm:min-w-0">
            <Button 
              variant={activeTab === 'rides' ? 'default' : 'outline'}
              onClick={() => setActiveTab('rides')}
              className={`${activeTab === 'rides' ? 'bg-amber-500 hover:bg-amber-600' : ''} whitespace-nowrap text-sm sm:text-base`}
              size="sm"
            >
              <Car size={16} className="mr-1 sm:mr-2" />
              <span className="hidden xs:inline">Ride </span>Requests
            </Button>
            <Button 
              variant={activeTab === 'earnings' ? 'default' : 'outline'}
              onClick={() => { setActiveTab('earnings'); fetch1099Data(); }}
              className={`${activeTab === 'earnings' ? 'bg-amber-500 hover:bg-amber-600' : ''} whitespace-nowrap text-sm sm:text-base`}
              size="sm"
            >
              <Banknote size={16} className="mr-1 sm:mr-2" />
              Earnings<span className="hidden sm:inline"> & Payouts</span>
            </Button>
            <Button 
              variant={activeTab === 'referrals' ? 'default' : 'outline'}
              onClick={() => setActiveTab('referrals')}
              className={`${activeTab === 'referrals' ? 'bg-purple-500 hover:bg-purple-600' : ''} whitespace-nowrap text-sm sm:text-base`}
              size="sm"
            >
              <Gift size={16} className="mr-1 sm:mr-2" />
              Referrals
            </Button>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Main Content Area */}
          <div className="space-y-6">
            {/* Active Ride Panel */}
            {activeRide && (
              <>
                <ActiveRidePanel 
                  ride={activeRide} 
                  driverId={currentDriver?.id}
                  onUpdate={() => {
                    fetchActiveRide();
                    fetchConnectStatus();
                  }}
                  backendUrl={BACKEND_URL}
                />
                
                {/* Chat Button */}
                {activeRide.user_id && !showChat && (
                  <Button
                    onClick={() => setShowChat(true)}
                    className="w-full bg-amber-100 hover:bg-amber-200 text-amber-700 border border-amber-300"
                    data-testid="driver-open-chat-btn"
                  >
                    <MessageCircle size={18} className="mr-2" />
                    Message Passenger
                  </Button>
                )}

                {/* Chat Panel */}
                {showChat && activeRide.user_id && (
                  <RideChat
                    rideId={activeRide.id}
                    senderId={currentDriver?.id}
                    senderType="driver"
                    senderName={currentDriver?.name || 'Driver'}
                    recipientName={activeRide.passenger_name || 'Rider'}
                    backendUrl={BACKEND_URL}
                    onClose={() => setShowChat(false)}
                    newMessage={newChatMessage}
                  />
                )}
              </>
            )}
            
            {activeTab === 'rides' && (
              <Card className="p-6 border-2" data-testid="available-rides-section">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-semibold text-xl">Available Requests</h3>
                  {isOnline && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={fetchAvailableRides}
                      disabled={isRefreshing}
                      data-testid="refresh-rides-btn"
                    >
                      <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                    </Button>
                  )}
                </div>
                {!isOnline ? (
                  <div className="text-center py-12 text-gray-500">
                    <Car size={48} className="mx-auto mb-4 opacity-50" />
                    <p>Go online to start accepting rides</p>
                  </div>
                ) : availableRides.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <Clock size={48} className="mx-auto mb-4 opacity-50" />
                    <p>Waiting for ride requests...</p>
                    <p className="text-sm mt-2">You&apos;ll get instant notifications when new rides are available</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {availableRides
                      .filter(ride => {
                        // Filter based on service mode
                        if (serviceMode === 'both') return true;
                        const rideType = ride.service_type || ride.type || 'ride';
                        if (serviceMode === 'rides') return rideType !== 'delivery';
                        if (serviceMode === 'deliveries') return rideType === 'delivery';
                        return true;
                      })
                      .map((ride) => (
                      <Card key={ride.id} className={`p-4 border-2 hover:border-amber-400 transition-all ${ride.is_scheduled || ride.scheduled_time ? 'border-purple-400 bg-purple-50' : ''}`} data-testid={`ride-request-${ride.id}`}>
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-2">
                            {ride.type === 'ride' ? (
                              <Car size={20} className="text-amber-600" />
                            ) : (
                              <Package size={20} className="text-slate-600" />
                            )}
                            <span className="font-semibold">{ride.type === 'ride' ? 'Ride Request' : 'Delivery Request'}</span>
                            {(ride.is_scheduled || ride.scheduled_time) && (
                              <span className="px-2 py-0.5 bg-purple-600 text-white text-xs font-bold rounded-full flex items-center gap-1">
                                <Clock size={12} />
                                SCHEDULED
                              </span>
                            )}
                          </div>
                          <span className="text-lg font-bold text-green-600">${ride.estimatedPrice || ride.estimated_price}</span>
                        </div>
                        
                        {/* Scheduled Time Banner */}
                        {(ride.is_scheduled || ride.scheduled_time) && (
                          <div className="mb-3 p-3 bg-gradient-to-r from-purple-100 to-purple-200 rounded-lg border border-purple-300">
                            <div className="flex items-center gap-2">
                              <Clock size={18} className="text-purple-700" />
                              <div>
                                <p className="text-xs text-purple-600 font-medium">PICKUP TIME</p>
                                <p className="text-lg font-bold text-purple-800">
                                  {new Date(ride.scheduled_time).toLocaleString('en-US', {
                                    weekday: 'short',
                                    month: 'short',
                                    day: 'numeric',
                                    hour: 'numeric',
                                    minute: '2-digit',
                                    hour12: true
                                  })}
                                </p>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        <div className="space-y-2 mb-4">
                          <div className="flex items-start gap-2 text-sm">
                            <MapPin size={16} className="text-amber-600 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-700">{ride.pickup || ride.pickup_address}</span>
                          </div>
                          
                          {/* Display stops if any */}
                          {ride.stops && ride.stops.length > 0 && (
                            <div className="ml-4 border-l-2 border-dashed border-amber-300 pl-4 space-y-2">
                              {ride.stops.map((stop, idx) => (
                                <div key={idx} className="flex items-start gap-2 text-sm">
                                  <div className="w-5 h-5 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                                    <span className="text-xs font-bold text-amber-700">{idx + 1}</span>
                                  </div>
                                  <span className="text-gray-600">{stop.address}</span>
                                </div>
                              ))}
                            </div>
                          )}
                          
                          <div className="flex items-start gap-2 text-sm">
                            <MapPin size={16} className="text-slate-600 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-700">{ride.dropoff || ride.dropoff_address}</span>
                          </div>
                          
                          {/* Display special instructions if any */}
                          {ride.special_instructions && (
                            <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                              <div className="flex items-start gap-2">
                                <FileText size={16} className="text-blue-600 mt-0.5 flex-shrink-0" />
                                <div>
                                  <p className="text-xs font-semibold text-blue-700 uppercase">Customer Instructions</p>
                                  <p className="text-sm text-blue-800 mt-1">{ride.special_instructions}</p>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="flex items-center justify-between mb-4">
                          <span className="text-sm text-gray-600">{ride.distance || ride.estimated_distance} mi • {ride.estimatedTime || ride.estimated_time} min</span>
                        </div>

                        <div className="flex gap-2">
                          <Button 
                            className="flex-1 bg-gradient-to-r from-amber-500 to-yellow-600 hover:from-amber-600 hover:to-yellow-700"
                            onClick={() => handleAcceptRide(ride.id)}
                            data-testid={`accept-ride-${ride.id}`}
                          >
                            Accept
                          </Button>
                          <Button 
                            variant="outline"
                            className="flex-1 border-red-300 text-red-600 hover:bg-red-50"
                            onClick={() => handleDeclineRide(ride.id)}
                            data-testid={`decline-ride-${ride.id}`}
                          >
                            Decline
                          </Button>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </Card>
            )}

            {activeTab === 'earnings' && (
              /* Earnings & Payouts Tab */
              <div className="space-y-6">
                {/* PayPal Email Setup Card */}
                <Card className="p-6 border-2 border-green-200 bg-green-50" data-testid="paypal-setup-card">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                      <Mail className="text-green-600" size={24} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg text-green-800">PayPal Payout Email</h3>
                      <p className="text-sm text-green-700">Receive your 70% earnings via PayPal</p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <Label className="text-green-800">Your PayPal Email</Label>
                      <div className="flex gap-2 mt-1">
                        <Input 
                          type="email"
                          value={paypalEmail}
                          onChange={(e) => setPaypalEmail(e.target.value)}
                          placeholder="your-paypal@email.com"
                          className="border-green-300 focus:border-green-500"
                        />
                        <Button 
                          onClick={handleUpdatePaypalEmail}
                          disabled={isUpdatingPaypal || !paypalEmail}
                          className="bg-green-600 hover:bg-green-700 whitespace-nowrap"
                        >
                          {isUpdatingPaypal ? (
                            <Loader2 className="animate-spin" size={16} />
                          ) : (
                            'Save'
                          )}
                        </Button>
                      </div>
                      <p className="text-xs text-green-700 mt-2">
                        💰 You receive <span className="font-bold">70%</span> of every completed trip
                      </p>
                    </div>
                    
                    {currentDriver?.paypalEmail && (
                      <div className="bg-white border border-green-300 rounded-lg p-3">
                        <div className="flex items-center gap-2">
                          <CheckCircle className="text-green-600" size={16} />
                          <span className="text-sm text-green-800">
                            Payouts will be sent to: <span className="font-semibold">{currentDriver.paypalEmail}</span>
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </Card>

                {/* Bank Account Setup Card */}
                <Card className="p-6 border-2" data-testid="bank-setup-card">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                      <Banknote className="text-amber-600" size={24} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg">Bank Account</h3>
                      <p className="text-sm text-gray-600">Receive payouts to your bank</p>
                    </div>
                  </div>

                  {!connectStatus?.has_account ? (
                    <div className="space-y-4">
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                        <div className="flex items-start gap-2">
                          <AlertCircle className="text-amber-600 mt-0.5" size={18} />
                          <div>
                            <p className="text-sm text-amber-900 font-medium">Bank account not set up</p>
                            <p className="text-sm text-amber-800">Add your bank account to receive payouts from your rides.</p>
                          </div>
                        </div>
                      </div>
                      <Button 
                        className="w-full bg-gradient-to-r from-amber-500 to-yellow-600 hover:from-amber-600 hover:to-yellow-700"
                        onClick={handleSetupBankAccount}
                        disabled={isSettingUpBank}
                        data-testid="setup-bank-btn"
                      >
                        {isSettingUpBank ? (
                          <>
                            <Loader2 className="mr-2 animate-spin" size={16} />
                            Setting up...
                          </>
                        ) : (
                          <>
                            <CreditCard className="mr-2" size={16} />
                            Set Up Bank Account
                          </>
                        )}
                      </Button>
                    </div>
                  ) : connectStatus.payouts_enabled ? (
                    <div className="space-y-4">
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <div className="flex items-center gap-2">
                          <CheckCircle className="text-green-600" size={18} />
                          <p className="text-sm text-green-900 font-medium">Bank account connected - Ready for payouts!</p>
                        </div>
                      </div>
                      <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                        <span className="text-gray-600">Pending Payout</span>
                        <span className="text-2xl font-bold text-green-600">${(connectStatus.pending_payout || 0).toFixed(2)}</span>
                      </div>
                      <Button 
                        className="w-full bg-green-600 hover:bg-green-700"
                        onClick={handleRequestPayout}
                        disabled={isRequestingPayout || (connectStatus.pending_payout || 0) <= 0}
                        data-testid="request-payout-btn"
                      >
                        {isRequestingPayout ? (
                          <>
                            <Loader2 className="mr-2 animate-spin" size={16} />
                            Processing...
                          </>
                        ) : (
                          <>
                            <DollarSign className="mr-2" size={16} />
                            Request Payout
                          </>
                        )}
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                        <div className="flex items-start gap-2">
                          <AlertCircle className="text-orange-600 mt-0.5" size={18} />
                          <div>
                            <p className="text-sm text-orange-900 font-medium">Verification pending</p>
                            <p className="text-sm text-orange-800">Please complete your account setup to enable payouts.</p>
                          </div>
                        </div>
                      </div>
                      <Button 
                        className="w-full"
                        variant="outline"
                        onClick={handleSetupBankAccount}
                        disabled={isSettingUpBank}
                      >
                        Complete Setup
                      </Button>
                    </div>
                  )}
                </Card>

                {/* Payout History */}
                <Card className="p-6 border-2" data-testid="payout-history-card">
                  <h3 className="font-semibold text-lg mb-4">Payout History</h3>
                  {payoutHistory.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <DollarSign size={32} className="mx-auto mb-2 opacity-50" />
                      <p>No payouts yet</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {payoutHistory.map((payout) => (
                        <div key={payout.id} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                          <div>
                            <p className="font-medium">${payout.amount}</p>
                            <p className="text-xs text-gray-500">{new Date(payout.created_at).toLocaleDateString()}</p>
                          </div>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${payout.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                            {payout.status}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>

                {/* 1099 Tax Forms Section */}
                <Card className="p-6 border-2 border-amber-200" data-testid="tax-1099-card">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-lg flex items-center gap-2">
                      <FileText className="text-amber-500" size={20} />
                      1099 Tax Forms
                    </h3>
                    <div className="flex items-center gap-2">
                      <select
                        value={selected1099Year}
                        onChange={(e) => {
                          setSelected1099Year(parseInt(e.target.value));
                          fetch1099Data(parseInt(e.target.value));
                        }}
                        className="border rounded-lg px-2 py-1 text-sm"
                      >
                        {[2024, 2025, 2026].map(year => (
                          <option key={year} value={year}>{year}</option>
                        ))}
                      </select>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => fetch1099Data()}
                        disabled={is1099Loading}
                      >
                        {is1099Loading ? <Loader2 className="animate-spin" size={16} /> : <RefreshCw size={16} />}
                      </Button>
                    </div>
                  </div>

                  {is1099Loading ? (
                    <div className="text-center py-6">
                      <Loader2 className="animate-spin mx-auto mb-2" size={24} />
                      <p className="text-sm text-gray-500">Loading tax data...</p>
                    </div>
                  ) : driver1099Data ? (
                    <div className="space-y-4">
                      {/* Earnings Summary */}
                      <div className="grid grid-cols-2 gap-3">
                        <div className="p-3 bg-blue-50 rounded-lg">
                          <p className="text-xs text-blue-600">Earnings</p>
                          <p className="font-bold text-blue-700">${driver1099Data.total_earnings?.toLocaleString() || '0'}</p>
                        </div>
                        <div className="p-3 bg-green-50 rounded-lg">
                          <p className="text-xs text-green-600">Tips</p>
                          <p className="font-bold text-green-700">${driver1099Data.total_tips?.toLocaleString() || '0'}</p>
                        </div>
                        <div className="p-3 bg-amber-50 rounded-lg col-span-2">
                          <p className="text-xs text-amber-600">Total Gross Income (Box 1)</p>
                          <p className="font-bold text-lg text-amber-700">${driver1099Data.gross_income?.toLocaleString() || '0'}</p>
                        </div>
                      </div>

                      {/* Form Status */}
                      {driver1099Data.requires_1099 === false ? (
                        <div className="p-3 bg-gray-50 rounded-lg text-center">
                          <p className="text-sm text-gray-600">
                            1099 forms are only required for earnings ≥ $600
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            Your {selected1099Year} earnings: ${driver1099Data.gross_income || 0}
                          </p>
                        </div>
                      ) : driver1099Data.sent_to_driver ? (
                        <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                          <div className="flex items-center gap-2 text-green-700">
                            <CheckCircle size={18} />
                            <span className="font-medium">1099-NEC form available</span>
                          </div>
                          <p className="text-sm text-green-600 mt-1">
                            Your {selected1099Year} tax form has been generated by J-Style Services.
                          </p>
                          <p className="text-xs text-gray-500 mt-2">
                            Payer: {driver1099Data.payer_name || 'J-Style Services LLC'}
                          </p>
                        </div>
                      ) : driver1099Data.generated_at ? (
                        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                          <div className="flex items-center gap-2 text-amber-700">
                            <Clock size={18} />
                            <span className="font-medium">1099-NEC form generated</span>
                          </div>
                          <p className="text-sm text-amber-600 mt-1">
                            Your form has been prepared and will be available soon.
                          </p>
                        </div>
                      ) : (
                        <div className="p-3 bg-gray-50 rounded-lg text-center">
                          <p className="text-sm text-gray-600">
                            1099 forms for {selected1099Year} have not been generated yet.
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            Forms are typically available by January 31st for the previous tax year.
                          </p>
                        </div>
                      )}

                      {/* Total Rides */}
                      <div className="text-center pt-2 border-t">
                        <p className="text-sm text-gray-500">
                          Total trips completed in {selected1099Year}: <span className="font-semibold">{driver1099Data.total_rides || 0}</span>
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-6 text-gray-500">
                      <FileText size={32} className="mx-auto mb-2 opacity-50" />
                      <p>No tax data available for {selected1099Year}</p>
                      <Button
                        variant="outline"
                        size="sm"
                        className="mt-3"
                        onClick={() => fetch1099Data()}
                      >
                        Load Tax Data
                      </Button>
                    </div>
                  )}
                </Card>
              </div>
            )}

            {/* Referrals Tab */}
            {activeTab === 'referrals' && (
              <div className="space-y-6">
                <Card className="p-6 border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-pink-50">
                  <h2 className="text-xl font-bold text-purple-800 mb-4 flex items-center gap-2">
                    <Gift size={24} /> Driver Referral Program
                  </h2>
                  
                  {/* Referral Code */}
                  <div className="bg-white p-4 rounded-xl border-2 border-purple-300 mb-6">
                    <p className="text-sm text-purple-600 mb-2">Your Referral Code</p>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="text-3xl font-bold text-purple-800 tracking-wider flex-1">
                        {referralStats?.referral_code || 'Loading...'}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-purple-300 hover:bg-purple-100"
                        onClick={() => {
                          navigator.clipboard.writeText(referralStats?.referral_code || '');
                          toast({ title: "Copied!", description: "Referral code copied to clipboard" });
                        }}
                      >
                        <Copy size={16} className="mr-1" /> Copy
                      </Button>
                    </div>
                    
                    {/* Shareable Link */}
                    <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
                      <p className="text-xs text-purple-700 font-medium mb-2">Share Your Referral Link</p>
                      <div className="flex items-center gap-2">
                        <Input
                          readOnly
                          value={`${window.location.origin}/driver/signup?ref=${referralStats?.referral_code || ''}`}
                          className="text-xs bg-white border-purple-200 text-purple-800"
                        />
                        <Button
                          size="sm"
                          className="bg-purple-500 hover:bg-purple-600 flex-shrink-0"
                          onClick={() => {
                            const link = `${window.location.origin}/driver/signup?ref=${referralStats?.referral_code || ''}`;
                            navigator.clipboard.writeText(link);
                            toast({ title: "Link Copied!", description: "Share this link with other drivers" });
                          }}
                        >
                          <Copy size={14} className="mr-1" /> Copy Link
                        </Button>
                      </div>
                    </div>
                    
                    <p className="text-xs text-gray-500 mt-3">
                      Share this code or link with other drivers. When they sign up and complete rides, you both earn rewards!
                    </p>
                  </div>
                  
                  {/* Stats Grid */}
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="bg-white p-4 rounded-xl border text-center">
                      <p className="text-3xl font-bold text-purple-700">{referralStats?.total_referrals || 0}</p>
                      <p className="text-sm text-gray-600">Total Referrals</p>
                    </div>
                    <div className="bg-white p-4 rounded-xl border text-center">
                      <p className="text-3xl font-bold text-green-600">${referralStats?.total_earned || 0}</p>
                      <p className="text-sm text-gray-600">Total Earned</p>
                    </div>
                    <div className="bg-white p-4 rounded-xl border text-center">
                      <p className="text-3xl font-bold text-amber-600">{referralStats?.pending_referrals || 0}</p>
                      <p className="text-sm text-gray-600">Pending</p>
                    </div>
                    <div className="bg-white p-4 rounded-xl border text-center">
                      <p className="text-3xl font-bold text-blue-600">${referralStats?.pending_earnings || 0}</p>
                      <p className="text-sm text-gray-600">Pending Earnings</p>
                    </div>
                  </div>
                  
                  {/* How it Works */}
                  <div className="bg-purple-100 p-4 rounded-xl">
                    <h3 className="font-semibold text-purple-800 mb-2">How It Works</h3>
                    <ol className="text-sm text-purple-700 space-y-2">
                      <li className="flex items-start gap-2">
                        <span className="w-5 h-5 bg-purple-600 text-white rounded-full flex items-center justify-center text-xs flex-shrink-0">1</span>
                        <span>Share your referral code with drivers you know</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="w-5 h-5 bg-purple-600 text-white rounded-full flex items-center justify-center text-xs flex-shrink-0">2</span>
                        <span>They enter your code when signing up</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="w-5 h-5 bg-purple-600 text-white rounded-full flex items-center justify-center text-xs flex-shrink-0">3</span>
                        <span>Once they complete {referralStats?.min_rides_for_reward || 10} rides, you both earn ${referralStats?.reward_per_referral || 25}!</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="w-5 h-5 bg-green-600 text-white rounded-full flex items-center justify-center text-xs flex-shrink-0">4</span>
                        <span>Admin will process your payout manually via PayPal or direct transfer</span>
                      </li>
                    </ol>
                  </div>
                  
                  {/* Payout Info */}
                  <div className="bg-green-50 border border-green-200 p-4 rounded-xl mt-4">
                    <div className="flex items-start gap-2">
                      <DollarSign className="text-green-600 mt-0.5" size={18} />
                      <div>
                        <p className="font-medium text-green-800">Payout Information</p>
                        <p className="text-sm text-green-700 mt-1">
                          Referral rewards are paid out manually by J-Style Services. Once your referred driver completes the required rides, your reward will be added to &quot;Total Earned&quot; and processed during the next payout cycle.
                        </p>
                      </div>
                    </div>
                  </div>
                </Card>
              </div>
            )}
          </div>

          {/* Driver Profile & Earnings */}
          <div className="space-y-6">
            {/* Profile */}
            <Card className="p-6 border-2" data-testid="driver-profile-card">
              <div className="flex items-center gap-4 mb-6">
                {(currentDriver?.profile_photo || localStorage.getItem('driver_photo')) ? (
                  <img 
                    src={(() => {
                      const photo = currentDriver?.profile_photo || localStorage.getItem('driver_photo');
                      return photo?.startsWith('http') ? photo : `${BACKEND_URL}${photo}`;
                    })()}
                    alt={currentDriver?.name}
                    className="w-16 h-16 rounded-full object-cover border-2 border-amber-400"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.style.display = 'none';
                      if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div className={`w-16 h-16 bg-gradient-to-br from-amber-500 to-yellow-600 rounded-full items-center justify-center ${(currentDriver?.profile_photo || localStorage.getItem('driver_photo')) ? 'hidden' : 'flex'}`}>
                  <User className="text-white" size={32} />
                </div>
                <div>
                  <h3 className="font-bold text-xl">{currentDriver?.name}</h3>
                  <p className="text-gray-600">{currentDriver?.vehicleModel}</p>
                  <div className="flex items-center gap-1 mt-1">
                    <Star size={16} className="fill-amber-400 text-amber-400" />
                    <span className="font-semibold">{currentDriver?.rating}</span>
                    <span className="text-sm text-gray-500">({currentDriver?.totalRides} trips)</span>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex justify-between py-2 border-b">
                  <span className="text-gray-600">Vehicle</span>
                  <span className="font-semibold">{currentDriver?.vehicleModel}</span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="text-gray-600">License Plate</span>
                  <span className="font-semibold">{currentDriver?.licensePlate}</span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="text-gray-600">Member Since</span>
                  <span className="font-semibold">{currentDriver?.joinDate ? new Date(currentDriver.joinDate).toLocaleDateString() : 'N/A'}</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-gray-600">Status</span>
                  <span className="flex items-center gap-1 text-green-600 font-semibold">
                    <CheckCircle size={16} />
                    {currentDriver?.status}
                  </span>
                </div>
              </div>
            </Card>

            {/* Quick Earnings Summary */}
            <Card className="p-6 bg-gradient-to-br from-slate-800 via-slate-900 to-slate-800 text-white border-2 border-slate-700 relative overflow-hidden" data-testid="earnings-card">
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-amber-500/20 to-yellow-500/20 rounded-full blur-3xl" />
              <div className="relative z-10">
                <h3 className="font-semibold text-lg mb-4">Total Earnings</h3>
                <div className="text-4xl font-bold mb-2 text-amber-400">${(connectStatus?.total_earnings || currentDriver?.pendingPayout || 0).toFixed(2)}</div>
                <p className="text-slate-300 mb-4">Lifetime earnings</p>
                <Button 
                  className="w-full bg-gradient-to-r from-amber-500 to-yellow-600 hover:from-amber-600 hover:to-yellow-700 text-white"
                  onClick={() => setActiveTab('earnings')}
                  data-testid="view-earnings-btn"
                >
                  View Earnings & Payouts
                </Button>
              </div>
            </Card>
          </div>
        </div>
      </div>

      {/* Install App Prompt */}
      <InstallPrompt />

      {/* Scheduled Ride/Delivery Alert Modal */}
      {scheduledRideAlert && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="scheduled-ride-alert">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl animate-pulse-once">
            {/* Header */}
            <div className="text-center mb-6">
              <div className={`w-20 h-20 rounded-full mx-auto flex items-center justify-center mb-4 animate-bounce ${scheduledRideAlert.isDelivery ? 'bg-gradient-to-br from-blue-400 to-indigo-500' : 'bg-gradient-to-br from-amber-400 to-yellow-500'}`}>
                {scheduledRideAlert.isDelivery ? <Package size={40} className="text-white" /> : <Clock size={40} className="text-white" />}
              </div>
              <h2 className="text-2xl font-bold text-slate-800">⏰ SCHEDULED {scheduledRideAlert.isDelivery ? 'DELIVERY' : 'RIDE'}</h2>
              <p className={`font-semibold mt-1 ${scheduledRideAlert.isDelivery ? 'text-blue-600' : 'text-amber-600'}`}>Arrive On Time!</p>
            </div>
            
            {/* Pickup Time - Large & Prominent */}
            <div className={`rounded-xl p-4 mb-4 border-2 ${scheduledRideAlert.isDelivery ? 'bg-gradient-to-r from-blue-100 to-indigo-100 border-blue-300' : 'bg-gradient-to-r from-amber-100 to-yellow-100 border-amber-300'}`}>
              <p className={`text-sm font-medium mb-1 ${scheduledRideAlert.isDelivery ? 'text-blue-700' : 'text-amber-700'}`}>PICKUP TIME</p>
              <p className={`text-3xl font-bold ${scheduledRideAlert.isDelivery ? 'text-blue-800' : 'text-amber-800'}`}>{scheduledRideAlert.scheduledTime}</p>
            </div>
            
            {/* Passenger & Location */}
            <div className="space-y-3 mb-6">
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                <User className="text-blue-500" size={20} />
                <div>
                  <p className="text-xs text-gray-500">{scheduledRideAlert.isDelivery ? 'CUSTOMER' : 'PASSENGER'}</p>
                  <p className="font-semibold">{scheduledRideAlert.passengerName}</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <MapPin className="text-green-500 mt-0.5" size={20} />
                <div>
                  <p className="text-xs text-gray-500">PICKUP LOCATION</p>
                  <p className="font-medium text-sm">{scheduledRideAlert.pickupAddress}</p>
                </div>
              </div>
            </div>
            
            {/* Important Note */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-blue-800">
                <strong>📢 Important:</strong> Please arrive at the pickup location 5 minutes before the scheduled time to ensure excellent service.
              </p>
            </div>
            
            {/* Action Buttons */}
            <div className="flex gap-3">
              <Button 
                className="flex-1 bg-gradient-to-r from-amber-500 to-yellow-600 hover:from-amber-600 hover:to-yellow-700 text-white font-semibold py-3"
                onClick={() => {
                  setScheduledRideAlert(null);
                  // Could open maps here
                }}
              >
                Got It!
              </Button>
              <Button 
                variant="outline"
                className="border-amber-500 text-amber-600"
                onClick={() => announceScheduledPickup(scheduledRideAlert.rawTime, scheduledRideAlert.passengerName, scheduledRideAlert.isDelivery)}
              >
                🔊 Repeat
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DriverPortal;
